import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';
import { isHexColour, isPaletteId, type PaletteId } from './palettes';

export type SavedItem = { i: string; x: number; y: number; w: number; h: number };
export type TodayPrefs = { layout: SavedItem[] | null; hidden: string[]; palette: PaletteId; accent: string | null };
export type OrgDefault = { layout: SavedItem[]; hidden: string[]; updatedBy?: string };

/** The one account allowed to set the layout everyone starts from (also enforced in firestore.rules). */
export const LAYOUT_OWNER_EMAIL = 'shetajatin18@gmail.com';

const DEFAULT_PREFS: TodayPrefs = { layout: null, hidden: [], palette: 'saffron', accent: null };

const clean = (raw: any): TodayPrefs => ({
  layout: Array.isArray(raw?.layout)
    ? raw.layout
        .filter((l: any) => l && typeof l.i === 'string' && [l.x, l.y, l.w, l.h].every((n) => Number.isFinite(Number(n))))
        .map((l: any) => ({ i: l.i, x: Number(l.x), y: Number(l.y), w: Number(l.w), h: Number(l.h) }))
    : null,
  hidden: Array.isArray(raw?.hidden) ? raw.hidden.filter((v: any) => typeof v === 'string') : [],
  palette: isPaletteId(raw?.palette) ? raw.palette : 'saffron',
  accent: isHexColour(raw?.accent) ? raw.accent : null,
});

const readLocal = (key: string): TodayPrefs | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? clean(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

/**
 * Per-user layout and colours for the Office Today page. Saved in the browser at once and in
 * Firestore `userPreferences/{uid}` so they follow the user to other devices; if Firestore refuses
 * the write (rule not deployed yet), the browser copy still works.
 */
export function useTodayPrefs(uid: string | null | undefined) {
  const db = useMemo(() => getFirestore(), []);
  const localKey = `office-today:prefs:${uid || 'anon'}`;
  const [prefs, setPrefs] = useState<TodayPrefs>(() => readLocal(localKey) || DEFAULT_PREFS);
  const saveTimer = useRef<number | undefined>(undefined);
  const warned = useRef(false);
  const [orgDefault, setOrgDefault] = useState<OrgDefault | null>(null);

  // The shared default layout, used by anyone who has not arranged their own.
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'appDefaults', 'officeToday'))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = clean(snap.data());
        if (data.layout?.length) setOrgDefault({ layout: data.layout, hidden: data.hidden, updatedBy: String((snap.data() as any)?.updatedBy || '') });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [db]);

  const saveAsDefault = useCallback(
    async (layout: SavedItem[], hidden: string[], email: string) => {
      const next: OrgDefault = { layout: layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })), hidden, updatedBy: email };
      await setDoc(doc(db, 'appDefaults', 'officeToday'), { ...next, updatedAt: serverTimestamp() });
      setOrgDefault(next);
    },
    [db]
  );

  useEffect(() => {
    setPrefs(readLocal(localKey) || DEFAULT_PREFS);
    if (!uid) return;
    let cancelled = false;
    getDoc(doc(db, 'userPreferences', uid))
      .then((snap) => {
        const remote = snap.exists() ? (snap.data() as any)?.officeToday : null;
        if (cancelled || !remote) return;
        const next = clean(remote);
        setPrefs(next);
        try { window.localStorage.setItem(localKey, JSON.stringify(next)); } catch { /* ignore */ }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [db, localKey, uid]);

  const update = useCallback(
    (patch: Partial<TodayPrefs>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        try { window.localStorage.setItem(localKey, JSON.stringify(next)); } catch { /* ignore */ }
        if (uid) {
          window.clearTimeout(saveTimer.current);
          saveTimer.current = window.setTimeout(() => {
            setDoc(doc(db, 'userPreferences', uid), { officeToday: { ...next, updatedAt: serverTimestamp() } }, { merge: true }).catch((err) => {
              if (!warned.current) console.warn('Office Today: layout saved in this browser only', err?.code || err);
              warned.current = true;
            });
          }, 800);
        }
        return next;
      });
    },
    [db, localKey, uid]
  );

  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  return { prefs, update, reset: () => update({ layout: null, hidden: [] }), orgDefault, saveAsDefault };
}
